import React, { useState } from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';
import { officeLocations, contactFormOptions } from '../mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    userType: '',
    city: '',
    projectType: '',
    message: '',
    source: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        userType: '',
        city: '',
        projectType: '',
        message: '',
        source: ''
      });
    }, 1500);
  };

  return (
    <div className="bg-[#f5f2ed] pt-24">
      {/* Hero */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-['Playfair_Display'] text-[#1a1a1a] mb-6 fade-in">
            Start a Conversation
          </h1>
          <p className="text-xl text-[#9c9590] leading-relaxed fade-in" style={{ animationDelay: '0.2s' }}>
            Whether you're an architect with drawings ready, or a homeowner with the beginning of an idea — we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-white">
        <div className="max-w-3xl mx-auto">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="userType" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                    I am a *
                  </label>
                  <select
                    id="userType"
                    name="userType"
                    required
                    value={formData.userType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all"
                  >
                    <option value="">Select...</option>
                    {contactFormOptions.userTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                    City *
                  </label>
                  <select
                    id="city"
                    name="city"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all"
                  >
                    <option value="">Select...</option>
                    {contactFormOptions.cities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  required
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all"
                >
                  <option value="">Select...</option>
                  {contactFormOptions.projectTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                  Tell us about your project
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all resize-none"
                ></textarea>
              </div>

              <div>
                <label htmlFor="source" className="block text-sm tracking-wide text-[#1a1a1a] mb-2">
                  How did you hear about us? *
                </label>
                <select
                  id="source"
                  name="source"
                  required
                  value={formData.source}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#f5f2ed] text-[#1a1a1a] border-none focus:ring-2 focus:ring-[#b8986e] outline-none transition-all"
                >
                  <option value="">Select...</option>
                  {contactFormOptions.sources.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1a1a1a] text-[#f5f2ed] px-12 py-4 text-base tracking-wide hover:bg-[#2a2a2a] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Inquiry'}
              </button>
            </form>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-[#b8986e] rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-['Playfair_Display'] text-[#1a1a1a] mb-4">
                Thank you
              </h2>
              <p className="text-[#9c9590] mb-8">
                We'll be in touch within 24 hours.
              </p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="text-[#b8986e] hover:text-[#1a1a1a] transition-colors"
              >
                Send another inquiry
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Office Locations */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-4xl font-['Playfair_Display'] text-[#1a1a1a] mb-12 text-center">
            Our Offices
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {officeLocations.map((office) => (
              <div key={office.id} className="bg-white p-8">
                <h3 className="text-2xl font-['Playfair_Display'] text-[#1a1a1a] mb-6">
                  {office.label}
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3 text-[#9c9590]">
                    <MapPin size={20} className="mt-1 flex-shrink-0" />
                    <span>{office.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#9c9590]">
                    <Phone size={20} className="flex-shrink-0" />
                    <a href={`tel:${office.phone}`} className="hover:text-[#b8986e] transition-colors">
                      {office.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-[#9c9590]">
                    <Mail size={20} className="flex-shrink-0" />
                    <a href={`mailto:${office.email}`} className="hover:text-[#b8986e] transition-colors">
                      {office.email}
                    </a>
                  </div>
                </div>
                <div className="h-[300px] overflow-hidden">
                  <iframe
                    src={office.mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Map of ${office.city}`}
                  ></iframe>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Response Time */}
      <section className="py-12 bg-[#1a1a1a] text-center">
        <p className="text-[#9c9590] tracking-wide">
          We respond to every inquiry within 24 hours.
        </p>
      </section>
    </div>
  );
};

export default Contact;
